import { useTranslation } from "react-i18next";
import { notification, Button } from "antd";
import type { FormInstance } from "antd";
import { normalizeApiError } from "@/shared/utils/errors/normalizeApiError";
import { useNavigate, useParams } from "react-router-dom";
import { translateBackendError } from "@/shared/lib/errors/translateError";
import { claimError } from "@/shared/lib/errors/errorClaim";

type ErrorType =
  | "BUSINESS_RULE"
  | "VALIDATION"
  | "PERMISSION"
  | "SERVER"
  | "NETWORK"
  | "CONFLICT"
  | "STOCK";

export interface MutationErrorHandlerOptions {
  form?: FormInstance;
  onErrorHandled?: (error: any) => void;
}

export interface MutationErrorHandler {
  handleMutationError: (error: any) => void;
}

export function useMutationErrorHandler(
  options: MutationErrorHandlerOptions = {},
): MutationErrorHandler {
  const { form, onErrorHandled } = options;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const classifyError = (
    statusCode: number,
    hasFieldErrors: boolean,
    errorType?: string,
    code?: string,
  ): ErrorType => {
    if (errorType === "BUSINESS_RULE") return "BUSINESS_RULE";
    if (statusCode === 0) return "NETWORK";
    if (statusCode === 403) return "PERMISSION";
    // 409 — a guarded update (e.g. cancelling order items, receiving a
    // return) found the record had already moved since the page loaded.
    // Not a server bug: it's "please refresh and retry", so it gets its
    // own bucket rather than falling into the generic SERVER toast.
    if (statusCode === 409) return "CONFLICT";
    // A 400 with this stable code is a stock shortage, not a malformed
    // request — checked before the generic hasFieldErrors branch below,
    // since the backend reports it under `errors: [{ field: "general" }]`
    // (no per-field errors) and it deserves its own actionable message.
    if (code === "INSUFFICIENT_STOCK") return "STOCK";
    if (statusCode === 400 && hasFieldErrors) return "VALIDATION";
    if (statusCode >= 500) return "SERVER";
    return "SERVER";
  };

  const handleMutationError = (error: any): void => {
    // Claim it before doing anything else. The global fallback in
    // queryClient.ts has already run and scheduled its own toast for the next
    // macrotask; this runs in the rejection microtask, so claiming here is
    // what stops one failure producing two stacked notifications. See
    // shared/lib/errors/errorClaim.ts.
    claimError(error);
    try {
      const normalized = normalizeApiError(error);
      const hasFieldErrors = normalized.fieldErrors.size > 0;
      const errorType = classifyError(
        normalized.statusCode,
        hasFieldErrors,
        normalized.errorType,
        normalized.code,
      );

      switch (errorType) {
        case "BUSINESS_RULE":
          if (normalized.businessRuleType === "CANNOT_REVERT_TWICE") {
            notification.warning({
              message: t("orders.cannotRevertTwice"),
              description: t("orders.cannotRevertTwiceMessage"),
              duration: 6,
            });
          } else if (normalized.businessRuleType === "PLAN_LIMIT_EXCEEDED") {
            const storeId = params.storeId;

            notification.warning({
              message: "Plan Limit Reached",
              description: (
                <div>
                  <p style={{ marginBottom: 12 }}>
                    {normalized.message || "You have reached your plan limit."}
                  </p>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      notification.destroy();
                      if (storeId) {
                        navigate(`/store/${storeId}/billing`);
                      } else {
                        navigate("/billing");
                      }
                    }}
                    className="p-4"
                  >
                    Upgrade Plan
                  </Button>
                </div>
              ),
              duration: 0,
            });
          } else {
            notification.warning({
              message: "Action Not Allowed",
              description:
                normalized.message || "This action violates a business rule.",
              duration: 5,
            });
          }
          break;

        case "VALIDATION":
          if (form) {
            const fields = Array.from(normalized.fieldErrors.entries()).map(
              ([name, errors]) => ({
                name,
                errors,
              }),
            );

            form.setFields(fields);

            if (fields.length > 0) {
              form.scrollToField(fields[0].name);
            }

            if (normalized.generalErrors.length > 0) {
              notification.error({
                message: t("errors.mutations.validationGeneral"),
                description: normalized.generalErrors[0],
                duration: 5,
              });
            }
          } else {
            notification.error({
              message: t("errors.mutations.validation"),
              description:
                normalized.generalErrors[0] ||
                t("errors.mutations.validationGeneral"),
              duration: 5,
            });
          }
          break;

        case "CONFLICT":
          // A warning, not an error toast: the request was correctly
          // rejected because the record moved, not because anything broke.
          // The description is the backend's own guidance (already written
          // for the merchant to read, e.g. "This order changed while you
          // were cancelling. Please try again") — translateBackendError
          // falls through to it since a bare ConflictException carries no
          // `code` for the errors.<code> lookup to key off.
          notification.warning({
            message: t("errors.mutations.conflict"),
            description: translateBackendError(error, t),
            duration: 6,
          });
          break;

        case "STOCK":
          // A warning, not an error toast: the request was correctly rejected
          // because stock ran out, not because anything broke. translateBackendError
          // resolves errors.INSUFFICIENT_STOCK and interpolates the backend's
          // `details.available`/`requested`, so the merchant sees the real count
          // instead of the generic "Not enough stock available".
          notification.warning({
            message: t("errors.mutations.stock"),
            description: translateBackendError(error, t),
            duration: 6,
          });
          break;

        case "PERMISSION":
          notification.error({
            message: t("errors.mutations.permission"),
            description: "You don't have permission to perform this action.",
            duration: 5,
          });
          break;

        case "SERVER":
          notification.error({
            message: t("errors.mutations.server"),
            description: translateBackendError(error, t),
            duration: 5,
          });
          // eslint-disable-next-line no-console
          if (import.meta.env.DEV) console.error("Server error:", error);
          break;

        case "NETWORK":
          notification.error({
            message: t("errors.mutations.network"),
            description: t("errors.network"),
            duration: 6,
          });
          break;

        default:
          notification.error({
            message: t("errors.mutations.server"),
            description: translateBackendError(error, t),
            duration: 5,
          });
          // eslint-disable-next-line no-console
          if (import.meta.env.DEV) console.error("Unknown error:", error);
      }

      if (onErrorHandled) {
        onErrorHandled(error);
      }
    } catch (handlerError) {
      // eslint-disable-next-line no-console
      if (import.meta.env.DEV) console.error("Error in mutation error handler:", handlerError);
      notification.error({
        message: t("errors.generic"),
        description: "An unexpected error occurred.",
        duration: 5,
      });
    }
  };

  return { handleMutationError };
}
